import React, { FunctionComponent, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Switch } from '@/atoms/switch'
import { ClientPortalClientPropertiesDTO } from '@/models/auth'
import { Space } from '@/styles'

import { NavigationMenu } from './navigation-menu'

interface Props {
  sideMenuLocked: boolean
  toggleSidebar: () => void
  clientPortalProperties?: ClientPortalClientPropertiesDTO
}

const Sidebar: FunctionComponent<Props> = ({ sideMenuLocked, toggleSidebar, clientPortalProperties }: Props) => {
  const { t } = useTranslation()
  const [isCollapsed, toggleCollapsed] = useState(true)
  const collapseSidebar = (): void => toggleCollapsed(true)
  const expandSidebar = (): void => toggleCollapsed(false)
  const showSwitch = !isCollapsed || sideMenuLocked

  return (
    <SidebarWrapper $sideMenuLocked={sideMenuLocked} onMouseEnter={expandSidebar} onMouseLeave={collapseSidebar}>
      <SidebarContent>
        <NavigationMenu clientPortalProperties={clientPortalProperties} />
        {showSwitch && (
          <Switch
            fieldProps={{ p: Space.S20 }}
            label={t(sideMenuLocked ? 'sidebar.unlock' : 'sidebar.lock')}
            onChange={toggleSidebar}
            checked={sideMenuLocked}
          />
        )}
      </SidebarContent>
    </SidebarWrapper>
  )
}

const SidebarContent = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.statusViolet};
  box-shadow: none;
  transition: width 195ms ease 0s, box-shadow 350ms cubic-bezier(0.4, 0, 0.6, 1) 0ms;
`

const SidebarWrapper = styled.div<{ $sideMenuLocked: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: ${({ $sideMenuLocked, theme }) => ($sideMenuLocked ? `${theme.drawerWidthOpen}px` : '64px')};
  height: calc(100% - 64px);
  margin-top: ${({ theme }) => `${theme.gutter * 16}px`};
  background: transparent;
  z-index: 101;
  transition: width 195ms ease 0s, padding 195ms ease 0s;
  overflow: hidden;

  &:hover {
    width: ${({ theme }) => `${theme.drawerWidthOpen}px`};

    ${SidebarContent} {
      border-right: none;
      box-shadow: ${({ $sideMenuLocked, theme }) => ($sideMenuLocked ? 'none' : theme.boxShadow)};
    }
  }
`

export default Sidebar
